import React, {useCallback, useEffect, useState} from 'react';

import {NumberInput} from 'src/components/input';
import {MAX_CONTRACT_FIELD_VALUE} from 'src/modules/profile/account/constants';
import {getObjectValues} from 'src/modules/profile/account/tabs/admin-account-api-v2/utils/getObjectValues';
import {LimitPeriod} from 'src/openapi/admin/models/LimitPeriod';
import {LimitType} from 'src/openapi/admin/models/LimitType';
import {PricingModel} from 'src/openapi/admin/models/PricingModel';

import Button from '@pb/kit-component-button';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {Integer} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';

import type {Locale} from '@pb/kit-context-locale';
import type {LimitsMap} from 'src/modules/profile/account/tabs/admin-account-api-v2/views/controllers/limits/services/typedef';
import type {ApiLimit} from 'src/openapi/admin/models/ApiLimit';

type Props = {
    locale: Locale,
    onAddLimit: (limit: ApiLimit) => void,
    pricingModel: PricingModel,
    limits: LimitsMap,
    disabled?: boolean,
    contractLevel?: boolean,
};

export const LimitItem = (props: Props) => {
    const {locale, pricingModel, limits, disabled, contractLevel} = props;

    const [limitType, setLimitType] = useState<LimitType | null>(null);
    const [quantity, setQuantity] = useState<number | undefined>();
    const [period, setPeriod] = useState<LimitPeriod | null>(null);
    const [addButtonDisabled, setAddButtonDisabled] = useState(true);

    const onChange = useCallback((value?: number) => {
        setQuantity(value);
    }, []);

    const onLimitTypeChange = useCallback((value: LimitType) => {
        setLimitType(value);
    }, []);

    const onLimitPeriodChange = useCallback((value: LimitPeriod) => {
        setPeriod(value);
    }, []);

    const onAddLimit = () => {
        if (limitType && period && quantity) {
            props.onAddLimit({limitType, quantity, period});
            setLimitType(null);
            setQuantity(undefined);
            setPeriod(null);
        }
    };

    useEffect(() => {
        if (limitType && period && quantity) {
            const limitExists = getObjectValues(limits)
                .some(({limitType: itemType, period: itemPeriod}) => limitType === itemType && period === itemPeriod);

            setAddButtonDisabled(limitExists);
        } else {
            setAddButtonDisabled(true);
        }
    }, [limitType, quantity, period, limits]);

    return (
        <Group direction="v" alignItems="start">
            <Group gap="none" alignItems="center">
                <div style={{width: 188}} className="pl-s pr-m">
                    <ComboBox
                        value={limitType || undefined}
                        placeholder="Select"
                        onChange={onLimitTypeChange}
                        disabled={disabled}>
                        {pricingModel !== PricingModel.INTEGRATION ?
                                <ComboboxItem value={LimitType.CREDITS} caption="Credits"/>
                            : null}
                        {pricingModel === PricingModel.SUBSCRIPTION ?
                                <ComboboxItem value={LimitType.OVERAGE} caption="Overage"/>
                            : null}
                        {contractLevel ? [
                            <ComboboxItem key="search" value={LimitType.SEARCH} caption="Search"/>,
                            <ComboboxItem key="update" value={LimitType.UPDATE} caption="Update"/>,
                        ] : <ComboboxItem value={LimitType.SEARCH_AND_UPDATE} caption="Search & Update"/>}
                        <ComboboxItem value={LimitType.PROFILES} caption="Profiles"/>
                    </ComboBox>
                </div>
                <div style={{width: 220}}>
                    <Group justify="space-between" gap="s" alignItems="center">
                        <NumberInput
                            width={105}
                            value={quantity}
                            onBlur={onChange}
                            readOnly={disabled}
                            maxValue={MAX_CONTRACT_FIELD_VALUE}
                            displayedValue={quantity ? Integer.format(quantity, locale) : ''}
                        />
                        <div>/</div>
                        <div style={{width: 130}}>
                            <ComboBox
                                value={period || undefined}
                                placeholder="Select"
                                onChange={onLimitPeriodChange}
                                disabled={disabled}>
                                <ComboboxItem value={LimitPeriod.MINUTE} caption="Minute"/>
                                <ComboboxItem value={LimitPeriod.HOUR} caption="Hour"/>
                                <ComboboxItem value={LimitPeriod.DAY} caption="Day"/>
                                <ComboboxItem value={LimitPeriod.MONTH} caption="Month"/>
                            </ComboBox>
                        </div>
                    </Group>
                </div>
            </Group>
            <Button disabled={addButtonDisabled} variant="Type.PRIMARY" onClick={onAddLimit}>Add</Button>
        </Group>
    );
};

